import { memo } from "react";

import Autocomplete from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";

import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import ImportExportIcon from "@mui/icons-material/ImportExport";

import { GenericObject } from "utility/misc";

import {
  Entry,
  Severities,
  SeveritiesAll,
  SeverityAll,
  SeverityEntry,
  SourceAll,
  SourceEntry,
  Sources,
  SourcesAll,
  StateAll,
  StateEntry,
  States,
  StatesAll,
} from "./data";

// -----------------------------------------------------------------------------

const OrganizationFindingsInboxFilters = memo(
  function OrganizationFindingsInboxFilters({
    severities,
    setSeverities,
    states,
    setStates,
    sources,
    setSources,
    sorting,
    setSorting,
    sortDirection,
    setSortDirection,
  }: Props): JSX.Element {
    //
    return (
      <Stack spacing={1} direction="row" sx={{ flex: 3 }}>
        <Autocomplete
          size="small"
          sx={{ flex: 1 }}
          multiple
          disableCloseOnSelect
          value={severities}
          options={Severities}
          getOptionLabel={toTitle}
          renderInput={renderInput("Severity")}
          renderOption={renderOptionAgainstFilters(severities)}
          onChange={updateFilters(setSeverities, SeveritiesAll, SeverityAll)}
        />
        <Autocomplete
          size="small"
          sx={{ flex: 1 }}
          multiple
          disableCloseOnSelect
          value={states}
          options={States}
          getOptionLabel={toTitle}
          renderInput={renderInput("State")}
          renderOption={renderOptionAgainstFilters(states)}
          onChange={updateFilters(setStates, StatesAll, StateAll)}
        />
        <Autocomplete
          size="small"
          sx={{ flex: 1 }}
          multiple
          disableCloseOnSelect
          value={sources}
          options={Sources}
          getOptionLabel={toTitle}
          renderInput={renderInput("Source")}
          renderOption={renderOptionAgainstFilters(sources)}
          onChange={updateFilters(setSources, SourcesAll, SourceAll)}
        />
        <FormControl size="small" sx={{ flex: 1 }}>
          <InputLabel>Sort By</InputLabel>
          <Select
            value={sorting}
            label="Sort By"
            onChange={(event) => setSorting(event.target.value)}
          >
            <MenuItem value="asset">Asset</MenuItem>
            <MenuItem value="event_time">Event Time</MenuItem>
            <MenuItem value="name">Name</MenuItem>
            <MenuItem value="severity">Severity</MenuItem>
            <MenuItem value="source">Source</MenuItem>
            <MenuItem value="state">State</MenuItem>
          </Select>
        </FormControl>
        <IconButton
          aria-label="sort-direction-button"
          sx={{ alignSelf: "center" }}
          onClick={() => {
            setSortDirection(sortDirection === 0 ? 1 : 0);
          }}
        >
          <ImportExportIcon />
        </IconButton>
      </Stack>
    );
  }
);

export default OrganizationFindingsInboxFilters;

// -----------------------------------------------------------------------------

function renderOptionAgainstFilters(filters: Entry[]) {
  return (props: GenericObject, option: Entry) => (
    // nosemgrep: typescript.react.security.audit.react-html-element-spreading.react-html-element-spreading
    <li {...props} style={{ paddingLeft: 8 }}>
      <Checkbox
        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
        checkedIcon={<CheckBoxIcon fontSize="small" />}
        style={{ marginRight: 8, padding: 0 }}
        checked={filters.includes(option)}
      />
      {option.title}
    </li>
  );
}

function renderInput(label: string) {
  return (params: GenericObject) => {
    return <TextField {...params} label={label} />;
  };
}

// -----------------------------------------------------------------------------

function updateFilters(
  setFilters: Function,
  AllFilters: Entry[],
  AllFilter: Entry
) {
  return (_event: GenericObject, newValue: Entry[]) => {
    setFilters((filters: Entry[]) => {
      const addingAll =
        (!filters.includes(AllFilter) && newValue.includes(AllFilter)) ||
        AllFilters.every((entry: Entry) => newValue.includes(entry));

      //
      return addingAll || newValue.length < 1
        ? [AllFilter]
        : newValue.filter(outAllEntries);
    });
  };
}

// -----------------------------------------------------------------------------

function toTitle(option: GenericObject) {
  return option.title;
}

function outAllEntries(option: Entry): boolean {
  return !option?.all;
}

// -----------------------------------------------------------------------------

interface Props {
  severities: SeverityEntry[];
  setSeverities: Function;
  states: StateEntry[];
  setStates: Function;
  sources: SourceEntry[];
  setSources: Function;
  sorting: string;
  setSorting: Function;
  sortDirection: number;
  setSortDirection: Function;
}
